import { useContext, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash-es';

import { AppStateContext } from '@/state/AppStateContext';
import { fileUploadAndIndex, imageUpload } from '@/api/api';
import { ChatMessage, ChatModel, ErrorResponsePayload, FileContent, FileUploadResponse, IndividualFileChat, ProtectedTabularDescriptor, StreamErrorDetails } from '@/api/models';
import { chatModels } from '@/constants/models';
import { handleCheckStatus, setEffectiveModel } from '@/utils/utils';
import { getErrorDetails, getErrorSummary } from '@/utils/errorMessages';
import { pollFileProcessingStatus } from '@/utils/fileUploadStatus';
import { FileSnapshotError, snapshotFile, snapshotFiles } from '@/utils/fileSnapshot';
import {
  FILE_UPLOAD_CONSTRAINTS,
  createFileList,
  getAllowedFileMimeTypes,
  getAllowedFileTypes,
  isFileUploadDisabled,
  isUploadDisabled,
  showFileValidationErrors,
  showImageValidationError,
  validateFiles,
  validateImage,
} from '@/utils/fileValidation';

export interface UploadResult {
  initialFile: boolean;
  addedFile: boolean;
  filesStr: string;
  filename: string;
  imageBlobURL: string;
  directFiles: boolean;
  conversationId: string;
  effectiveModel: ChatModel;
  error: boolean;
  // Populated only when error is true and the server returned a parseable body.
  // A transport failure (fileUploadAndIndex/imageUpload resolving to null) or an
  // empty/non-JSON response leaves both undefined so callers fall back to a
  // generic message.
  errorMessage?: string;
  errorDetails?: StreamErrorDetails;
}

/**
 * Best-effort parse of a failed upload response's JSON body. The response may be
 * empty, non-JSON, or the connection may have dropped mid-body (the same
 * net::ERR_EMPTY_RESPONSE case documented on processUploads's outer try/catch),
 * so any parse failure degrades to "no details" rather than throwing.
**/
const parseUploadErrorResponse = async (resp: Response): Promise<{ errorMessage?: string; errorDetails?: StreamErrorDetails }> => {
  try {
    const data = await resp.json() as Partial<ErrorResponsePayload>;
    const errorMessage = typeof data?.error === 'string' ? data.error : undefined;
    const errorDetails = getErrorDetails(errorMessage, data?.error_details, resp.status);
    return {
      errorMessage: getErrorSummary(errorMessage, errorDetails),
      errorDetails,
    };
  } catch {
    const fallback = resp.statusText || (resp.status ? `Request failed with status ${resp.status}.` : undefined);
    const errorDetails = getErrorDetails(fallback, undefined, resp.status || undefined);
    return { errorMessage: getErrorSummary(fallback, errorDetails), errorDetails };
  }
};

/**
 * Status-poll backoff. A flat interval means a job that finished a second after
 * the previous poll still shows a spinner until the next one fires, so start
 * tight and ramp for the genuinely long jobs.
**/
const POLL_INTERVALS_MS = [1000, 2000, 3000, 5000, 8000];
const POLL_MAX_INTERVAL_MS = 10000;

export type FileUploadStage = 'queued' | 'uploading' | 'uploaded' | 'processing';

export interface UploadFileProgress {
  name: string;
  size: number;
  stage: FileUploadStage;
}

export interface UploadProgress {
  files: UploadFileProgress[];
  /** Which part of the wait this is: bytes on the wire, then server-side work. */
  phase: 'uploading' | 'extracting' | 'indexing' | 'processing';
  /** Content bytes sent and the total to send. Zero-length while phase !== 'uploading'. */
  sentBytes: number;
  totalBytes: number;
}

/**
 * Derives each file's stage from the cumulative bytes sent.
 *
 * Every file goes up in one multipart request, so the transfer reports a single
 * aggregate byte count and never per-file progress. The browser does write the
 * parts in the order they were appended, though, so the cumulative position is a
 * faithful answer to "which file is on the wire right now".
**/
export const stageFilesByBytes = (files: UploadFileProgress[], sentBytes: number): UploadFileProgress[] => {
  let start = 0;
  return files.map((file) => {
    const end = start + file.size;
    const stage: FileUploadStage =
      sentBytes >= end ? 'uploaded'
      : sentBytes > start ? 'uploading'
      : 'queued';
    start = end;
    return { ...file, stage };
  });
};

export function useFileUpload() {
  const appStateContext = useContext(AppStateContext);
  const [indexing, setIndexing] = useState(false);
  // Per-file state for the in-flight upload, so callers can show what is happening
  // to each file rather than one undifferentiated spinner for the whole batch.
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const defaultModel = appStateContext?.state.currentModel || chatModels.find(model => model.default) || chatModels[0];

  useEffect(() => {
    let active = true;
    const uploadState = appStateContext?.state.fileUpload?.current;

    const handleDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      if (isUploadDisabled(appStateContext)) return;
      e.preventDefault();
      dragCounter.current++;
      if (dragCounter.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      if (isUploadDisabled(appStateContext)) return;
      e.preventDefault();
      dragCounter.current--;
      if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDrop = async (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      if (isUploadDisabled(appStateContext)) return;
      const fileUploadDisabled = isFileUploadDisabled(appStateContext);
      e.preventDefault();
      if (uploadState?.isPreparing) return;

      appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: '' });
      dragCounter.current = 0;
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (!files) return;
      const selectedFiles = Array.from(files);

      const filesArray = selectedFiles.filter(file => {
        if (getAllowedFileMimeTypes().includes(file.type)) return true;
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        return getAllowedFileTypes().includes(ext);
      });

      if ((appStateContext?.state.fileUpload?.current?.files?.length || 0) === 0) {
        if (filesArray.length === 1 && filesArray[0].type.startsWith('image/')) {
          const imageFile = filesArray[0];
          if (validateImage(imageFile, appStateContext)) {
            uploadState?.invalidateImagePreparations?.();
            const imageSelectionRevision = uploadState?.imageSelectionRevision;
            uploadState?.beginPreparation?.();
            try {
              const snapshot = await snapshotFile(imageFile);
              if (!active || imageSelectionRevision !== uploadState?.imageSelectionRevision) return;
              const dt = createFileList([snapshot]);
              if (appStateContext?.state.imageUpload?.current) {
                appStateContext.state.imageUpload.current.files = dt.files;
                appStateContext.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: appStateContext.state.imageUpload });
              }
            } catch (error) {
              console.error('Could not prepare selected file upload:', error);
              if (active) alert(error instanceof FileSnapshotError ? error.message : 'Could not prepare selected files. Please select them again.');
            } finally {
              uploadState?.endPreparation?.();
            }
          } else showImageValidationError(appStateContext);
          return;
        }
      }

      if (!fileUploadDisabled && (appStateContext?.state.imageUpload?.current?.files?.length || 0) === 0) {
        const currentFileCount = appStateContext?.state.fileUpload?.current?.files?.length || 0;
        const remainingSlots = Math.max(0, FILE_UPLOAD_CONSTRAINTS.MAX_FILES - (appStateContext?.state.individualFileChat?.length || 0));
        const availableSlots = Math.max(0, remainingSlots - currentFileCount);
        const result = validateFiles(filesArray, availableSlots);
        if (result.oversized.length > 0 || result.overLimit.length > 0) showFileValidationErrors(result);
        if (result.valid.length > 0 && uploadState) {
          const fileSelectionRevision = uploadState.fileSelectionRevision;
          uploadState.beginPreparation?.();
          try {
            const snapshots = await snapshotFiles(result.valid);
            if (!active || fileSelectionRevision !== uploadState.fileSelectionRevision) return;
            const currentFiles = uploadState.files ? Array.from<File>(uploadState.files) : [];
            const latestCount = currentFiles.length;
            const latestSlots = Math.max(0, remainingSlots - latestCount);
            const latestResult = validateFiles([...currentFiles, ...snapshots], remainingSlots);
            const additions = latestResult.valid.slice(latestCount, latestCount + latestSlots);
            if (latestResult.oversized.length > 0 || latestResult.overLimit.length > 0) showFileValidationErrors(latestResult);
            if (additions.length > 0) {
              uploadState.files = createFileList([...currentFiles, ...additions]).files;
              appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: appStateContext.state.fileUpload });
            }
          } catch (error) {
            console.error('Could not prepare selected file upload:', error);
            if (active) alert(error instanceof FileSnapshotError ? error.message : 'Could not prepare selected files. Please select them again.');
          } finally {
            uploadState.endPreparation?.();
          }
        }
      }
    };

    const handleDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragover', handleDragOver);

    return () => {
      active = false;
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragover', handleDragOver);
    };
  }, [
    appStateContext?.state.selectedTool,
    appStateContext?.state.individualFileChat,
    appStateContext?.state.currentModel,
    appStateContext?.state.currentCustomGPT,
    appStateContext?.state.fileUpload,
    appStateContext?.state.imageUpload,
  ]);

  /**
   * Process any pending file or image upload before sending a prompt.
   * Mutates userMessage in-place (context, files, filename, direct_files).
   * @param conversationId  Current conversation ID (may be updated by server for new conversations)
   * @param userMessage     The user message being constructed — mutated with file metadata
   * @param existingMessageCount  Number of existing messages (0 = new conversation)
   */
  const processUploads = async (
    conversationId: string,
    userMessage: ChatMessage,
    existingMessageCount: number
  ): Promise<UploadResult> => {
    let initialFile = false;
    let addedFile = false;
    let filesStr = '';
    let filename = '';
    let imageBlobURL = '';
    let directFiles = false;
    let resolvedConversationId = conversationId;
    let effectiveModel = defaultModel;

    if (appStateContext?.state.currentChat?.direct_files) directFiles = appStateContext.state.currentChat.direct_files;

    // ── Document / multi-file upload ──────────────────────────────────────────
    if (appStateContext?.state.fileUpload?.current?.files?.length) {
      const selectedFiles = Array.from<File>(appStateContext.state.fileUpload.current.files);
      const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
      setIndexing(true);
      setUploadProgress({
        files: selectedFiles.map((file) => ({ name: file.name, size: file.size, stage: 'queued' })),
        phase: 'uploading',
        sentBytes: 0,
        totalBytes,
      });
      let fileUploadError = false;
      let errorMessage: string | undefined;
      let errorDetails: StreamErrorDetails | undefined;

      const formData = new FormData();
      formData.append('conversation_id', conversationId);
      // Send the selected model's deployment so the backend can size the
      // tabular direct-context token cap to the model's context window.
      const modelDeployment = appStateContext?.state.currentModel?.deployment;
      if (modelDeployment) formData.append('model', modelDeployment);
      for (let i = 0; i < appStateContext.state.fileUpload.current.files.length; i++) {
        formData.append('files', appStateContext.state.fileUpload.current.files[i]);
      }
      const file_resp = await fileUploadAndIndex(
        formData,
        ({ sent, total }) => {
          // Wire bytes carry multipart overhead, so scale them back onto the file
          // sizes rather than comparing the two counts directly.
          const sentBytes = total > 0 ? Math.min(totalBytes, Math.round((sent / total) * totalBytes)) : 0;
          // Ignore anything arriving after the transfer handed off, so a late event
          // cannot rewrite the rows back to upload states under a later heading.
          setUploadProgress((prev) => prev?.phase !== 'uploading'
            ? prev
            : { ...prev, sentBytes, files: stageFilesByBytes(prev.files, sentBytes) });
        },
        // The server stores each file and extracts its text before it answers, so
        // this hand-off — not the response — is where that wait starts. Setting it
        // after the await would mean announcing extraction once it had finished.
        () => setUploadProgress((prev) => prev && {
          ...prev,
          phase: 'extracting',
          sentBytes: prev.totalBytes,
          files: prev.files.map((file) => ({ ...file, stage: 'processing' as const })),
        }),
      );

      try {
      if (file_resp && (file_resp.status === 200 || file_resp.status === 202)) {
        if (existingMessageCount === 0) initialFile = true;
        else addedFile = true;

        const data = await file_resp.json() as FileUploadResponse;
        resolvedConversationId = data['conversation_id'];
        filesStr = data['files_str'] || '';

        // The backend only overrides the model when it actually forces the turn onto a
        // different deployment (tabular Code Interpreter); otherwise it just echoes back
        // whatever we sent. Only apply the override — conversation-scoped, never global —
        // when it genuinely differs from what we sent. An unresolvable or missing
        // effective_model is a config mismatch, not an upload failure: degrade to the
        // current model rather than surfacing the misleading upload-error message.
        if (!data.effective_model) {
          console.warn('File upload response did not include an effective model deployment; keeping current model.');
        } else if (data.effective_model !== modelDeployment) {
          const resolved = setEffectiveModel(appStateContext, data.effective_model);
          if (!resolved) {
            console.warn(`File upload returned unknown effective model deployment "${data.effective_model}"; keeping current model.`);
          } else {
            effectiveModel = resolved;
          }
        }

        const advancedByFile: Record<string, boolean> = {};
        if (data['use_direct_context']) {
          const file_contents: FileContent[] = data.file_contents || [];
          file_contents.forEach((fc) => { advancedByFile[fc.filename] = !!fc.advanced_available; });
          const hasTabularMap = file_contents.some((fc) => fc.processing_mode === 'tabular_map');
          if (hasTabularMap) {
            userMessage.context = file_contents.map((fc) => ({ ...fc }));
            userMessage.protectedContext = file_contents
              .filter((fc) => fc.processing_mode === 'tabular_map')
              .map(({ filename }): ProtectedTabularDescriptor => ({ filename, kind: 'tabular_map' }));
          } else {
            userMessage.context = file_contents
              .map((fc) => `File: ${fc.filename}\n${'='.repeat(fc.filename.length + 6)}\n${fc.content}`)
              .join('\n\n');
          }
          directFiles = true;
        } else if (data.res &&
          data.res.statusQueryGetUri &&
          data.res.terminatePostUri
        ) {
          setUploadProgress((prev) => prev && { ...prev, phase: 'indexing' });
          const fileProcessed = await pollFileProcessingStatus(
            data['res']['statusQueryGetUri'],
            handleCheckStatus,
            (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds)),
            POLL_INTERVALS_MS,
            POLL_MAX_INTERVAL_MS,
          );
          if (!fileProcessed.fileProcessingSuccess) {
            fileUploadError = true;
            errorMessage = fileProcessed.errorMessage || (fileProcessed.error === 'polling'
              ? "We couldn't check whether your files finished processing. Please try uploading them again."
              : "File processing failed. Please try uploading your files again.");
            errorDetails = fileProcessed.errorDetails;
          }
        }

        if (!fileUploadError) {
          const isNewFileChat = (appStateContext?.state.individualFileChat?.length || 0) === 0;
          const individualFileChat: IndividualFileChat[] = [];
          const messageFiles: string[] = [];
          const localFiles = appStateContext.state.fileUpload.current.files;
          // The backend can normalize an upload's extension server-side (.xlsm/.xls ->
          // .xlsx) and stores/returns the effective name, not the one the browser sent.
          // files_str is built server-side from the same per-file loop that produced the
          // upload, in the same order, so it's the one response field guaranteed to be a
          // parallel array to localFiles regardless of which branch (direct-context vs
          // RAG) answered. Only trust that pairing when the counts match, though --
          // an older response shape or a partial failure could otherwise attach one
          // file's converted name to a different file's slot, which is worse than just
          // displaying the pre-conversion name.
          const effectiveNames = filesStr.split('|').filter(Boolean);
          const namesFromServer = effectiveNames.length === localFiles.length;
          for (let i = 0; i < localFiles.length; i++) {
            const effectiveName = namesFromServer ? effectiveNames[i] : localFiles[i].name;
            individualFileChat.push({
              id: resolvedConversationId,
              filename: effectiveName,
              imageEditing: false,
              advancedAvailable: advancedByFile[effectiveName] || false,
            });
            messageFiles.push(effectiveName);
          }
          appStateContext?.dispatch({
            type: isNewFileChat ? 'SET_INDIVIDUAL_FILE_CHAT' : 'UPDATE_INDIVIDUAL_FILE_CHAT',
            payload: individualFileChat,
          });
          if (appStateContext?.state.fileUpload?.current) appStateContext.state.fileUpload.current.value = '';
          if (!isEmpty(messageFiles)) userMessage.files = messageFiles.join('|');
          if (directFiles) userMessage.direct_files = directFiles;
        }
      } else {
        fileUploadError = true;
        // Status branch only (not the catch below) — the body here is still
        // unread, so this is the one place it's safe to consume it.
        if (file_resp) ({ errorMessage, errorDetails } = await parseUploadErrorResponse(file_resp));
      }
      } catch (e) {
        // A dropped/empty response (net::ERR_EMPTY_RESPONSE) or a malformed
        // body would otherwise throw out of processUploads — which runs
        // outside sendPrompt's try/catch — surfacing as an uncaught rejection
        // and leaving the indexing spinner stuck. Treat any failure here as a
        // recoverable upload error. The body may already be partially consumed
        // by the failed parse above, so don't attempt to re-read it.
        console.error('File upload processing failed:', e);
        fileUploadError = true;
        errorMessage = getErrorSummary(e);
        errorDetails = getErrorDetails(e);
      }

      setIndexing(false);
      setUploadProgress(null);
      if (fileUploadError) return { initialFile, addedFile, filesStr, filename, imageBlobURL, directFiles, conversationId: resolvedConversationId, effectiveModel, error: true, errorMessage, errorDetails };

    // ── Single image upload ───────────────────────────────────────────────────
    } else if (appStateContext?.state.imageUpload?.current?.files?.length) {
      // The image endpoint goes through plain fetch, which cannot report transfer
      // progress — so this path reports the server-side wait and no byte count.
      const imageFile = appStateContext.state.imageUpload.current.files[0];
      setIndexing(true);
      setUploadProgress({
        files: [{ name: imageFile.name, size: imageFile.size, stage: 'processing' }],
        phase: 'processing',
        sentBytes: 0,
        totalBytes: 0,
      });
      let fileUploadError = false;
      let errorMessage: string | undefined;
      let errorDetails: StreamErrorDetails | undefined;

      const formData = new FormData();
      formData.append('conversation_id', conversationId);
      formData.append('file', appStateContext.state.imageUpload.current.files[0]);
      const file_resp = await imageUpload(formData);

      if (file_resp && (file_resp.status === 200 || file_resp.status === 202)) {
        try {
          if (existingMessageCount === 0) initialFile = true;

          const data = await file_resp.json() as FileUploadResponse;
          resolvedConversationId = data['conversation_id'];
          filename = data.filename || '';

          if (data.plain_text) userMessage.context = data.plain_text;

          appStateContext?.dispatch({
            type: 'SET_INDIVIDUAL_FILE_CHAT',
            payload: [{ id: resolvedConversationId, filename, imageEditing: false }],
          });
          if (appStateContext?.state.imageUpload?.current) appStateContext.state.imageUpload.current.value = '';
          if (!isEmpty(filename)) userMessage.filename = filename;
        } catch (error) {
          fileUploadError = true;
          errorMessage = getErrorSummary(error);
          errorDetails = getErrorDetails(error);
        }
      } else {
        fileUploadError = true;
        if (file_resp) ({ errorMessage, errorDetails } = await parseUploadErrorResponse(file_resp));
      }

      setIndexing(false);
      setUploadProgress(null);
      if (fileUploadError) return { initialFile, addedFile, filesStr, filename, imageBlobURL, directFiles, conversationId: resolvedConversationId, effectiveModel, error: true, errorMessage, errorDetails };
    }

    return { initialFile, addedFile, filesStr, filename, imageBlobURL, directFiles, conversationId: resolvedConversationId, effectiveModel, error: false };
  };

  /**
   * When no new upload is pending, resolve the file context from an ongoing file conversation.
   */
  const resolveExistingContext = (initialFile: boolean, addedFile: boolean): { filesStr: string; filename: string } => {
    let filesStr = '';
    let filename = '';
    if (
      !initialFile && !addedFile &&
      appStateContext?.state.individualFileChat?.length &&
      !appStateContext.state.individualFileChat[0].imageEditing
    ) {
      const firstExt = appStateContext.state.individualFileChat[0].filename.split('.').pop()?.toLowerCase();
      if (firstExt === 'jpeg' || firstExt === 'jpg' || firstExt === 'png') {
        filename = appStateContext.state.individualFileChat[0].filename;
      } else {
        filesStr = Array.from(new Set(appStateContext.state.individualFileChat.map(f => f.filename))).join('|');
      }
    }
    return { filesStr, filename };
  };

  return { indexing, uploadProgress, isDragging, processUploads, resolveExistingContext };
}
